import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Router.
 *
 * The route table is **generated from the registry**, so every one of the 121
 * PRD pages is guaranteed to have a route: pages with a bespoke component get
 * it, the rest fall back to `SpecPage`, which states the responsibility and the
 * missing contract instead of inventing data.
 *
 * Two infrastructure routes sit outside the PRD inventory: `/login` (session /
 * role) and `/console/coverage` (the coverage report itself).
 */
import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ROUTES, routeCount } from "./routes/registry.js";
import { useSession } from "./lib/SessionContext.js";
import { Shell } from "./components/Shell.js";
import { SpecPage } from "./components/SpecPage.js";
import { Page } from "./components/ui.js";
import { LoginPage } from "./pages/Login.js";
import { CoveragePage } from "./pages/Coverage.js";
import { HomePage, CategoriesPage, CategoryDetailPage, SearchPage, ProductDetailPage } from "./pages/commerce/Public.js";
import { RfqNewPage, RfqListPage, RfqDetailPage, QuoteComparePage, QuoteDetailPage } from "./pages/commerce/Rfq.js";
import { BuyerDashboardPage, OrderListPage as BuyerOrderListPage, OrderDetailPage as BuyerOrderDetailPage, MembersPage, CustomizationsPage, CheckoutPage, DocumentsPage, } from "./pages/commerce/Workspace.js";
import { RndDashboardPage, ProjectListPage, ProjectDetailPage, DesignJobListPage, DesignJobDetailPage, DesignVersionsPage, DesignDfmPage, DesignCostingPage, FactoryMatchPage, SamplesPage, PublicationsPage, AiOpsPage, } from "./pages/rnd/Rnd.js";
import { ControlTowerPage, OrderListPage as ScmOrderListPage, OrderDetailPage as ScmOrderDetailPage, PlansPage, PlanDetailPage, PosPage, PoDetailPage, PaymentsPage, LedgerPage, InspectionsPage, ShipmentsPage, ShipmentDetailPage, WarehousesPage, CasesPage, } from "./pages/scm/Scm.js";
import { EventsPage, ExceptionsPage } from "./pages/admin/Events.js";
import { FactoryDashboardPage, OnboardingPage, ProfilePage, CapabilitiesPage, CapacityPage, EquipmentPage, FactoryProductsPage, SupplierRfqsPage, SupplierRfqDetailPage, WorkOrdersPage, WorkOrderDetailPage, QualityPage, DispatchPage, SettlementsPage, PurchaseOrdersPage, PurchaseOrderDetailPage, } from "./pages/mfg/Mfg.js";
import { WorkflowsPage, AdminCategoriesPage, AttributesPage, MarketsPage, IntegrationsPage, } from "./pages/admin/Admin.js";
import { SupplierProfilePage, NewDesignDetailPage, CustomizationDetailPage, SamplesPage as BuyerSamplesPage, CheckoutSuccessPage, ShipmentDetailPage as BuyerShipmentDetailPage, BillingPage, QualityPage as BuyerQualityPage, CasesPage as BuyerCasesPage, } from "./pages/commerce/Details.js";
import { SampleDetailPage, PublicationDetailPage } from "./pages/rnd/Details.js";
import { SuppliersPage, RefundsPage, ReconciliationPage, InspectionDetailPage, NcrPage as ScmNcrPage, ReceiptsPage, DispatchesPage, LogisticsExceptionsPage, TradeDocumentsPage, } from "./pages/scm/Details.js";
import { CertificationsPage, ProductEditPage, SkusPage, QuotesPage, QuoteDetailPage as FactoryQuoteDetailPage, ProductionPage, ExceptionsPage as FactoryExceptionsPage, NcrPage as FactoryNcrPage, AiAssistantPage, } from "./pages/mfg/Details.js";
/** route id → bespoke component. Anything missing renders <SpecPage>. */
const PAGES = {
    /* A1 — Commerce */
    "commerce.home": HomePage,
    "commerce.categories": CategoriesPage,
    "commerce.category-detail": CategoryDetailPage,
    "commerce.search": SearchPage,
    "commerce.product-detail": ProductDetailPage,
    "commerce.rfq-new": RfqNewPage,
    "commerce.workspace-rfqs": RfqListPage,
    "commerce.workspace-rfq-detail": RfqDetailPage,
    "commerce.quote-detail": QuoteDetailPage,
    "commerce.quote-compare": QuoteComparePage,
    "commerce.workspace": BuyerDashboardPage,
    "commerce.orders": BuyerOrderListPage,
    "commerce.order-detail": BuyerOrderDetailPage,
    "commerce.members": MembersPage,
    "commerce.customizations": CustomizationsPage,
    "commerce.checkout": CheckoutPage,
    "commerce.documents": DocumentsPage,
    "commerce.approvals": WorkflowsPage,
    "commerce.supplier-profile": SupplierProfilePage,
    "commerce.new-design-detail": NewDesignDetailPage,
    "commerce.customization-detail": CustomizationDetailPage,
    "commerce.samples": BuyerSamplesPage,
    "commerce.checkout-success": CheckoutSuccessPage,
    "commerce.shipment-detail": BuyerShipmentDetailPage,
    "commerce.billing": BillingPage,
    "commerce.quality": BuyerQualityPage,
    "commerce.cases": BuyerCasesPage,
    /* A2 — AI Product R&D */
    "rnd.dashboard": RndDashboardPage,
    "rnd.projects": ProjectListPage,
    "rnd.project-detail": ProjectDetailPage,
    "rnd.design-jobs": DesignJobListPage,
    "rnd.design-job-detail": DesignJobDetailPage,
    "rnd.design-detail": DesignVersionsPage,
    "rnd.design-versions": DesignVersionsPage,
    "rnd.dfm": DesignDfmPage,
    "rnd.costing": DesignCostingPage,
    "rnd.factory-match": FactoryMatchPage,
    "rnd.samples": SamplesPage,
    "rnd.publications": PublicationsPage,
    "rnd.ai-ops": AiOpsPage,
    "rnd.sample-detail": SampleDetailPage,
    "rnd.publication-detail": PublicationDetailPage,
    /* A3 — Supply Chain Control Tower */
    "scm.control-tower": ControlTowerPage,
    "scm.orders": ScmOrderListPage,
    "scm.order-detail": ScmOrderDetailPage,
    "scm.plans": PlansPage,
    "scm.plan-detail": PlanDetailPage,
    "scm.pos": PosPage,
    "scm.po-detail": PoDetailPage,
    "scm.payments": PaymentsPage,
    "scm.ledger": LedgerPage,
    "scm.inspections": InspectionsPage,
    "scm.shipments": ShipmentsPage,
    "scm.shipment-detail": ShipmentDetailPage,
    "scm.warehouses": WarehousesPage,
    "scm.cases": CasesPage,
    "scm.operations-exceptions": ExceptionsPage,
    "scm.suppliers": SuppliersPage,
    "scm.refunds": RefundsPage,
    "scm.reconciliation": ReconciliationPage,
    "scm.inspection-detail": InspectionDetailPage,
    "scm.ncr": ScmNcrPage,
    "scm.receipts": ReceiptsPage,
    "scm.dispatches": DispatchesPage,
    "scm.logistics-exceptions": LogisticsExceptionsPage,
    "scm.trade-documents": TradeDocumentsPage,
    /* A4 — Manufacturing Network */
    "mfg.dashboard": FactoryDashboardPage,
    "mfg.onboarding": OnboardingPage,
    "mfg.profile": ProfilePage,
    "mfg.capabilities": CapabilitiesPage,
    "mfg.capacity": CapacityPage,
    "mfg.equipment": EquipmentPage,
    "mfg.products": FactoryProductsPage,
    "mfg.rfqs": SupplierRfqsPage,
    "mfg.rfq-detail": SupplierRfqDetailPage,
    "mfg.work-orders": WorkOrdersPage,
    "mfg.work-order-detail": WorkOrderDetailPage,
    "mfg.quality": QualityPage,
    "mfg.dispatch": DispatchPage,
    "mfg.settlements": SettlementsPage,
    "mfg.purchase-orders": PurchaseOrdersPage,
    "mfg.purchase-order-detail": PurchaseOrderDetailPage,
    "mfg.certifications": CertificationsPage,
    "mfg.product-edit": ProductEditPage,
    "mfg.skus": SkusPage,
    "mfg.quotes": QuotesPage,
    "mfg.quote-detail": FactoryQuoteDetailPage,
    "mfg.production": ProductionPage,
    "mfg.exceptions": FactoryExceptionsPage,
    "mfg.ncr": FactoryNcrPage,
    "mfg.ai-assistant": AiAssistantPage,
    /* A5 — Platform admin */
    "admin.events": EventsPage,
    "admin.workflows": WorkflowsPage,
    "admin.categories": AdminCategoriesPage,
    "admin.attributes": AttributesPage,
    "admin.marketplaces": MarketsPage,
    "admin.integrations": IntegrationsPage,
};
function RouteElement({ spec }) {
    const C = PAGES[spec.id];
    return C ? _jsx(C, {}) : _jsx(SpecPage, { spec: spec });
}
function RequireSession({ children }) {
    const { session } = useSession();
    const loc = useLocation();
    if (!session)
        return _jsx(Navigate, { to: "/login", replace: true, state: { from: loc.pathname } });
    return _jsx(Shell, { children: children });
}
function NotFound() {
    return (_jsx(Page, { title: "\u9875\u9762\u4E0D\u5B58\u5728", titleEn: "404", subtitle: "\u8BE5\u8DEF\u7531\u4E0D\u5728 PRD \u9644\u5F55 A \u7684 121 \u4E2A\u9875\u9762\u6E05\u5355\u5185\u3002", children: _jsxs("div", { className: "notice notice--warn", children: ["\u68C0\u67E5\u5730\u5740\u662F\u5426\u6B63\u786E\uFF0C\u6216\u6253\u5F00 ", _jsx("a", { href: "/console/coverage", children: "\u9875\u9762\u8986\u76D6\u62A5\u544A" }), " \u67E5\u770B\u5B8C\u6574\u6E05\u5355\u3002"] }) }));
}
export function App() {
    useEffect(() => {
        // Fail fast if the registry ever drifts from the PRD's 121 pages.
        if (routeCount() !== 121) {
            console.error(`[vnmade] route registry has ${routeCount()} entries; PRD 附录 A requires 121`);
        }
    }, []);
    return (_jsxs(Routes, { children: [_jsx(Route, { path: "/login", element: _jsx(LoginPage, {}) }), ROUTES.map((spec) => (_jsx(Route, { path: spec.path, element: _jsx(RequireSession, { children: _jsx(RouteElement, { spec: spec }) }) }, spec.id))), _jsx(Route, { path: "/console/coverage", element: _jsx(RequireSession, { children: _jsx(CoveragePage, {}) }) }), _jsx(Route, { path: "*", element: _jsx(RequireSession, { children: _jsx(NotFound, {}) }) })] }));
}
